/**
 * BaseSimulationUtils.ts
 *
 * Shared utilities for common simulation logic across all simulation engines
 * Eliminates duplicate setup and calculation patterns
 */

import { SteuerParameter } from '@/types';
import { RateCalculationOptions } from './RateCalculationService';
// RateCalculationService will be loaded dynamically

export class BaseSimulationUtils {
  /**
   * Initialize empty inflation rates array for a new simulation
   */
  static initializeInflationRates(): number[] {
    return [];
  }

  /**
   * Calculate cumulative inflation factor for a given year
   */
  static calculateCumulativeInflationFactor(
    inflationRates: number[],
    currentYear: number,
    inflationOption: string
  ): number {
    if (inflationOption === 'keine-inflation') {
      return 1;
    }

    let cumulativeInflationFactor = 1;
    for (let y = 0; y < currentYear; y++) {
      if (y < inflationRates.length) {
        const rate = inflationRates[y];
        cumulativeInflationFactor *= 1 + rate / 100;
      }
    }
    return cumulativeInflationFactor;
  }

  /**
   * Ensure inflation rates are calculated and stored for all years up to current year
   */
  static async ensureInflationRatesCalculated(
    inflationRates: number[],
    currentYear: number,
    options: RateCalculationOptions,
    steuerParameter: SteuerParameter
  ): Promise<void> {
    if (options.inflationOption !== 'keine-inflation') {
      // Load RateCalculationService dynamically
      const { RateCalculationService } = await import('./RateCalculationService');

      // Ensure we have inflation rates for all previous years
      while (inflationRates.length < currentYear) {
        const year = inflationRates.length; // Use current array length as year index
        const rate = RateCalculationService.calculateInflationRate(options, steuerParameter, year);
        inflationRates.push(rate);
      }
    }
  }

  /**
   * Calculate the number of withdrawal years for a simulation
   */
  static calculateWithdrawalYears(steuerParameter: SteuerParameter): number {
    return Math.max(0, steuerParameter.berechnungBisLebensalter - steuerParameter.retirementAge);
  }

  /**
   * Calculate Verbrauchstopf interest using average balance approach (50% due to monthly withdrawals)
   */
  static calculateVerbrauchstopfInterest(
    verbrauchstopfSaldo: number,
    interestRate: number
  ): number {
    // Money is withdrawn evenly throughout the year, so average balance is 50%
    const averageBalance = verbrauchstopfSaldo * 0.5;
    return averageBalance * (interestRate / 100);
  }

  /**
   * Apply interest/returns to pot balances
   */
  static applyInterestToPots(
    verbrauchstopfSaldo: number,
    zinstopfSaldo: number | undefined,
    investitionstopfSaldo: number,
    verbrauchstopfRate: number,
    zinstopfRate?: number,
    investitionstopfRate?: number
  ): {
    newVerbrauchstopfSaldo: number;
    newZinstopfSaldo?: number;
    newInvestitionstopfSaldo: number;
    verbrauchstopfInterest: number;
  } {
    // Apply interest to Verbrauchstopf using average balance
    const verbrauchstopfInterest = this.calculateVerbrauchstopfInterest(
      verbrauchstopfSaldo,
      verbrauchstopfRate
    );
    const newVerbrauchstopfSaldo = verbrauchstopfSaldo + verbrauchstopfInterest;

    // Apply interest to Zinstopf (if exists)
    const newZinstopfSaldo =
      zinstopfSaldo !== undefined && zinstopfRate !== undefined
        ? zinstopfSaldo * (1 + zinstopfRate / 100)
        : undefined;

    // Apply returns to Investitionstopf
    const newInvestitionstopfSaldo =
      investitionstopfRate !== undefined
        ? investitionstopfSaldo * (1 + investitionstopfRate / 100)
        : investitionstopfSaldo;

    return {
      newVerbrauchstopfSaldo,
      newZinstopfSaldo,
      newInvestitionstopfSaldo,
      verbrauchstopfInterest,
    };
  }

  /**
   * Check if simulation should terminate (no money left)
   */
  static shouldTerminateSimulation(verbrauchstopfSaldo: number, otherPotsSaldo: number): boolean {
    return verbrauchstopfSaldo === 0 && otherPotsSaldo === 0;
  }

  /**
   * Calculate required prefill amount for Verbrauchstopf
   */
  static calculatePrefillShortfall(jahresentnahme: number, verbrauchstopfSaldo: number): number {
    return Math.max(0, jahresentnahme - verbrauchstopfSaldo);
  }
}
